/**
 * IMPORTS
 */
import AxiosService from "@/infra/http/axios";
import type {
  ICandidatoConvite,
  IConvidarParticipantePayload,
  IEstadoConversa,
  IMensagemConversa,
  INotificacao,
  IParticipanteConversa,
  IResponderConversaPayload,
  IRespostaAcaoParticipante,
  IRespostaListaMensagens,
  IRespostaListaNotificacoes,
} from "@/interface/conversa-cliente";

const CABECALHOS_JSON = {
  "content-type": "application/json; charset=UTF-8",
  accept: "*/*",
};

// Rotas sem corpo recebem `{}`: com `null` e content-type JSON, o axios envia a string "null"
// e o body-parser da API responde 400 antes de chegar ao controller.

/**
 * Conversa do cliente logado com a equipe. A empresa vem do token (nunca da URL).
 */
const conversaClienteRepository = {
  async getListarMinhasMensagens(
    accessToken: string,
    pagina: number,
    itensPorPagina: number,
    busca?: string
  ): Promise<IRespostaListaMensagens> {
    const instancia = await AxiosService.createAxiosInstance(accessToken);
    const parametros = new URLSearchParams({
      page: String(pagina),
      per_page: String(itensPorPagina),
    });
    if (busca) parametros.set("search", busca);

    const resposta = await instancia.get(`minha-conversa/mensagens?${parametros.toString()}`, {
      headers: CABECALHOS_JSON,
    });
    return resposta.data;
  },

  async postResponderMinhasMensagens(
    accessToken: string,
    payload: IResponderConversaPayload
  ): Promise<IMensagemConversa> {
    const instancia = await AxiosService.createAxiosInstance(accessToken);
    const formData = new FormData();
    if (payload.conteudo.trim()) formData.append("conteudo", payload.conteudo.trim());
    for (const arquivo of payload.anexos) formData.append("anexos", arquivo, arquivo.name);

    const resposta = await instancia.post("minha-conversa/mensagens", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return resposta.data?.data;
  },

  /** Binário do anexo (download e prévia de imagem). */
  async getBuscarConteudoAnexo(
    accessToken: string,
    mensagemId: number,
    anexoId: number
  ): Promise<Blob> {
    const instancia = await AxiosService.createAxiosInstance(accessToken);
    const resposta = await instancia.get(
      `minha-conversa/mensagens/${mensagemId}/anexos/${anexoId}`,
      {
        responseType: "blob",
      }
    );
    return resposta.data;
  },

  /** Se a conversa foi iniciada, minha participação e (com acesso) os participantes. */
  async getEstadoConversa(accessToken: string): Promise<IEstadoConversa> {
    const instancia = await AxiosService.createAxiosInstance(accessToken);
    const resposta = await instancia.get("minha-conversa/participantes", {
      headers: CABECALHOS_JSON,
    });
    return resposta.data?.data;
  },

  async getListarCandidatosConvite(
    accessToken: string,
    busca?: string
  ): Promise<ICandidatoConvite[]> {
    const instancia = await AxiosService.createAxiosInstance(accessToken);
    const parametros = busca ? `?${new URLSearchParams({ search: busca }).toString()}` : "";
    const resposta = await instancia.get(`minha-conversa/participantes/candidatos${parametros}`, {
      headers: CABECALHOS_JSON,
    });
    return resposta.data?.data ?? [];
  },

  async postConvidarParticipante(
    accessToken: string,
    payload: IConvidarParticipantePayload
  ): Promise<IParticipanteConversa> {
    const instancia = await AxiosService.createAxiosInstance(accessToken);
    const resposta = await instancia.post("minha-conversa/participantes", payload, {
      headers: CABECALHOS_JSON,
    });
    return resposta.data?.data;
  },

  /** Só cancela convite `pendente` ou `recusado` (quem já participa é inativado). */
  async deleteRemoverParticipante(accessToken: string, participanteId: number): Promise<void> {
    const instancia = await AxiosService.createAxiosInstance(accessToken);
    await instancia.delete(`minha-conversa/participantes/${participanteId}`, {
      headers: CABECALHOS_JSON,
    });
  },

  /** Desativa o acesso de quem já participa (`aceito` → `inativo`); o histórico fica. */
  async postInativarParticipante(
    accessToken: string,
    participanteId: number
  ): Promise<IRespostaAcaoParticipante> {
    const instancia = await AxiosService.createAxiosInstance(accessToken);
    const resposta = await instancia.post(
      `minha-conversa/participantes/${participanteId}/inativar`,
      {},
      {
        headers: CABECALHOS_JSON,
      }
    );
    return resposta.data;
  },

  /** Devolve o acesso (`inativo` → `aceito`), sem novo convite. */
  async postReativarParticipante(
    accessToken: string,
    participanteId: number
  ): Promise<IRespostaAcaoParticipante> {
    const instancia = await AxiosService.createAxiosInstance(accessToken);
    const resposta = await instancia.post(
      `minha-conversa/participantes/${participanteId}/reativar`,
      {},
      {
        headers: CABECALHOS_JSON,
      }
    );
    return resposta.data;
  },

  /** Responde o convite recebido por notificação. */
  async postResponderConvite(
    accessToken: string,
    conviteId: number,
    resposta: "aceitar" | "recusar"
  ): Promise<IParticipanteConversa> {
    const instancia = await AxiosService.createAxiosInstance(accessToken);
    const retorno = await instancia.post(
      `convites-conversa/${conviteId}/${resposta}`,
      {},
      {
        headers: CABECALHOS_JSON,
      }
    );
    return retorno.data?.data;
  },

  async getListarNotificacoes(
    accessToken: string,
    pagina: number,
    itensPorPagina: number
  ): Promise<IRespostaListaNotificacoes> {
    const instancia = await AxiosService.createAxiosInstance(accessToken);
    const resposta = await instancia.get(`notificacoes?page=${pagina}&per_page=${itensPorPagina}`, {
      headers: CABECALHOS_JSON,
    });
    return resposta.data;
  },

  async getContarNotificacoesNaoLidas(accessToken: string): Promise<number> {
    const instancia = await AxiosService.createAxiosInstance(accessToken);
    const resposta = await instancia.get("notificacoes/nao-lidas/contagem", {
      headers: CABECALHOS_JSON,
    });
    return resposta.data?.data?.nao_lidas ?? 0;
  },

  async patchMarcarNotificacaoLida(accessToken: string, id: number): Promise<INotificacao> {
    const instancia = await AxiosService.createAxiosInstance(accessToken);
    const resposta = await instancia.patch(
      `notificacoes/${id}/lida`,
      {},
      {
        headers: CABECALHOS_JSON,
      }
    );
    return resposta.data?.data;
  },

  async patchMarcarTodasNotificacoesLidas(accessToken: string): Promise<number> {
    const instancia = await AxiosService.createAxiosInstance(accessToken);
    const resposta = await instancia.patch(
      "notificacoes/lidas",
      {},
      {
        headers: CABECALHOS_JSON,
      }
    );
    return resposta.data?.data?.marcadas ?? 0;
  },
};

/**
 * EXPORTS
 */
export { conversaClienteRepository };
